<template>
  <div class="w-100 my-5 py-4">
    <loader />
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex';
  import moment from 'moment';
  import { get } from '@/services/index.js';
  import {
    getPromotionUnavailableReason,
    resolvePromotionStayDate,
    getPromotionName,
    pickPromotionNames
  } from '@/utils/bePromotionRule.js';
  import { CLICK_ID_PARAMS } from '@/utils/trafficSource.js';

  const UNAVAILABLE_MESSAGE_KEYS = {
    inactive: 'hotel.filter.promotion-link.inactive',
    sale_period: 'hotel.filter.promotion-link.sale-period',
    quota: 'hotel.filter.promotion-link.quota',
    no_stay_date: 'hotel.filter.promotion-link.no-stay-date',
    not_found: 'hotel.filter.promotion-link.not-found'
  };
  const DETAIL_STATUS = { FOUND: 'found', NOT_FOUND: 'not_found', ERROR: 'error' };
  const SERVER_ERROR_STATUS = 500;
  const MEMBERSHIP_PROMOTION_TYPE = 'MEMBERSHIP';
  const UTM_PREFIX = 'utm_';
  const PROMO_LINK_TOAST_GROUP = 'promo-link';

  export default {
    name: 'BeDirectPromotion',
    metaInfo() {
      return { title: 'Loading...' };
    },
    computed: {
      ...mapGetters(['getMerchant', 'getBeFilter', 'getCurrentLanguage'])
    },
    async mounted() {
      const promotionId = this.$route.params.promotion_id;
      this.ensureSearchedStayDate();
      const { status, promotion } = await this.fetchPromotionDetail(promotionId);
      this.handlePromotionDetail(status, promotion, promotionId);

      // replace keeps the redirect page out of history so Back never bounces here
      this.$router.replace({ name: 'BERoom', query: this.getAttributionQuery() });
    },
    methods: {
      ...mapMutations([
        'setBePromotionFilter',
        'resetBePromotionFilter',
        'resetSelectedPromotion',
        'setSpecificBeFilter'
      ]),
      async fetchPromotionDetail(promotionId) {
        try {
          const apiParams = { merchant_id: this.getMerchant.id, promotion_id: promotionId };
          const apiResponse = await get('CORE_HOTEL', 'promotion-detail', apiParams);
          const { success, data } = apiResponse.data;
          if (success && data) return { status: DETAIL_STATUS.FOUND, promotion: data };
          return { status: DETAIL_STATUS.NOT_FOUND, promotion: null };
        } catch (error) {
          console.log('ERROR in fetchPromotionDetail() (BeDirectPromotion.vue) : ', error);
          return { status: this.getDetailErrorStatus(error), promotion: null };
        }
      },
      // pixels load after the merchant API and read fbclid/utm from the URL, so they must survive the redirect
      getAttributionQuery() {
        const { query } = this.$route;
        return Object.keys(query)
          .filter(key => key.indexOf(UTM_PREFIX) === 0 || CLICK_ID_PARAMS.includes(key))
          .reduce((attributionQuery, key) => ({ ...attributionQuery, [key]: query[key] }), {});
      },
      getDetailErrorStatus(error) {
        const { response } = error;
        const isServerUnreachable = !response || response.status >= SERVER_ERROR_STATUS;
        return isServerUnreachable ? DETAIL_STATUS.ERROR : DETAIL_STATUS.NOT_FOUND;
      },
      handlePromotionDetail(status, promotion, promotionId) {
        if (status === DETAIL_STATUS.FOUND) return this.applyPromotion(promotion);
        if (status === DETAIL_STATUS.NOT_FOUND) return this.rejectMissingPromotion();

        // the server could not answer, so the link still filters by id instead of dropping the promo
        this.setBePromotionFilter({ id: promotionId });
      },
      getRuleContext() {
        const { timezone, check_in_time_start: checkInTime } = this.getMerchant;
        const { check_in: checkIn, check_out: checkOut } = this.getBeFilter.date;
        return { now: new Date(), timezone, checkInTime, checkIn, checkOut };
      },
      // a corrupted persisted filter can lose its date, which would stall the redirect and the room page
      ensureSearchedStayDate() {
        const { date } = this.getBeFilter;
        if (date && date.check_in && date.check_out) return;
        const fallbackDate = { check_in: moment().toDate(), check_out: moment().add(1, 'd').toDate() };
        this.setSpecificBeFilter([{ key: 'date', value: fallbackDate }]);
      },
      applyPromotion(promotion) {
        const ruleContext = this.getRuleContext();
        const unavailableReason = getPromotionUnavailableReason(promotion, ruleContext);
        if (unavailableReason) return this.rejectPromotion(promotion, unavailableReason);

        const stayDate = resolvePromotionStayDate(promotion, ruleContext);
        if (!stayDate) return this.rejectPromotion(promotion, 'no_stay_date');

        this.commitPromotionFilter(promotion, stayDate);
      },
      rejectPromotion(promotion, reason) {
        this.resetBePromotionFilter();
        this.notifyUnavailable(getPromotionName(promotion, this.getCurrentLanguage), reason);
      },
      rejectMissingPromotion() {
        this.resetBePromotionFilter();
        this.notifyUnavailable('', 'not_found');
      },
      notifyUnavailable(title, reason) {
        this.$notify({
          group: PROMO_LINK_TOAST_GROUP,
          type: 'warn',
          title,
          text: this.$t(UNAVAILABLE_MESSAGE_KEYS[reason])
        });
      },
      commitPromotionFilter(promotion, stayDate) {
        const { promotion_type: promotionType, promo_code: promoCode } = promotion;
        const isPromoCode = promotionType === 'PROMO_CODE' && this.$isNotEmptyV2(promoCode);

        this.resetSelectedPromotion();
        this.setSpecificBeFilter([
          { key: 'date', value: stayDate },
          { key: 'promo_code', value: isPromoCode ? promoCode : '' }
        ]);

        // a promo-code promotion is searched by its code, exactly like a picked promo-code card
        if (isPromoCode) {
          this.resetBePromotionFilter();
          return;
        }
        this.setBePromotionFilter({
          id: promotion.id,
          names: pickPromotionNames(promotion),
          isMembership: promotionType === MEMBERSHIP_PROMOTION_TYPE
        });
      }
    }
  };
</script>



// WEBPACK FOOTER //
// src/pages/hotel/be/BeDirectPromotion.vue